/**
 * "What the user may change" — the admin's per-section switchboard.
 *
 * The layer stays the user's to edit; each row here closes ONE block of the
 * settings panel for them. That is the setting between the two blunt ones: the
 * user lock in the panel header hands the whole layer over or takes it away,
 * this decides which parts of it are theirs.
 *
 * Admin-only, and hidden entirely once the whole layer is user-locked — every
 * section is closed at that point, so a list of section switches would only
 * invite the admin to set something with no effect.
 */
import { IconLock, IconLockOpen } from '@tabler/icons-react';
import { PanelSection, T } from '../../ui/figma';
import { sectionsFor, toggleSectionLock } from '../../schema/sectionLocks';

export default function UserAccessSection({ el, updateElement, mode }) {
    if (mode !== 'admin' || !el || el.locked || el.editableByUser === false) return null;

    const rows = sectionsFor(el);
    const locked = Array.isArray(el.lockedSections) ? el.lockedSections : [];
    const set = (keys) => updateElement(el.id, { lockedSections: keys });

    return (
        <PanelSection
            title="What the user may change"
            actions={locked.length ? (
                <button
                    type="button"
                    className={`h-6 px-2 rounded-[5px] text-[10px] ${T.textSoft} hover:!text-black dark:hover:!text-white`}
                    onClick={() => set([])}
                    title="Open every section back up to the user"
                >
                    Unlock all
                </button>
            ) : null}
        >
            <p className={`mb-2 text-[10px] leading-snug ${T.textFaint}`}>
                A locked section is not shown to the user at all, and each one can
                also be locked from its own header above. Moving &amp; rotating and
                Layout additionally stop them dragging or resizing this layer.
            </p>
            {rows.map((s) => {
                const isLocked = locked.includes(s.key);
                return (
                    <button
                        key={s.key}
                        type="button"
                        onClick={() => set(toggleSectionLock(el, s.key))}
                        title={s.hint || (isLocked ? 'Open this section to the user' : 'Close this section to the user')}
                        className={`w-full flex items-center gap-2 h-7 px-1.5 -mx-1.5 rounded-[5px] text-left
                            ${T.hoverBg}`}
                    >
                        {isLocked
                            ? <IconLock size={12} className="shrink-0 text-[#0d99ff]" />
                            : <IconLockOpen size={12} className={`shrink-0 ${T.textFaint}`} />}
                        <span className={`flex-1 min-w-0 truncate text-[11px] ${isLocked ? T.textSoft : T.text}`}>
                            {s.label}
                        </span>
                        <span className={`shrink-0 text-[10px] ${isLocked ? 'text-[#0d99ff]' : T.textFaint}`}>
                            {isLocked ? 'Locked' : 'Open'}
                        </span>
                    </button>
                );
            })}
        </PanelSection>
    );
}
